import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-button text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-mint text-charcoal shadow-md hover:bg-[#00d4a0] hover:shadow-mintGlow hover:shadow-[0_0_0_6px]',
        destructive:
          'bg-danger text-white shadow-md hover:bg-[#ff6b6b] hover:shadow-lg focus-visible:ring-danger',
        outline:
          'border border-borderStrong bg-surface2 text-text hover:border-mintBorder hover:bg-surface2/80',
        secondary:
          'bg-surface2 text-text border border-borderStrong hover:border-mintBorder hover:bg-surface2/90',
        ghost: 'text-text hover:bg-surface2 hover:text-text',
        link: 'text-mint underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-button px-3',
        lg: 'h-11 rounded-button px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ComponentPropsWithoutRef<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
